import type { SkillIconName } from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import type { Skill } from '@atlassian/rovo-platform-ui-components/skills/types';

export interface SkillDirectoryItemNode {
	readonly __typename: string;
	readonly id: string;
	readonly slashCommand?: string | null;
	readonly name?: string | null;
	readonly description?: string | null;
	readonly iconUrl?: string | null;
	readonly color?: string | null;
	readonly integrationKey?: {
		readonly displayName?: string | null;
		readonly iconUrl?: string | null;
	} | null;
	/** GraphIntegrationSkillDirectoryItem.tags — capability + registry-projected dimensions */
	readonly tags?: ReadonlyArray<{
		readonly displayName: string;
	}> | null;
}

/**
 * Parses a single GraphIntegrationSkillDirectoryItem node into a Skill.
 * Returns null if the node is not a valid skill.
 */
export function parseSkillNode(node: SkillDirectoryItemNode | null | undefined): Skill | null {
	if (!node || node.__typename !== 'GraphIntegrationSkillDirectoryItem') {
		return null;
	}
	const displayName = node.integrationKey?.displayName ?? '';
	const tagDisplayNames = node.tags?.map((t) => t.displayName) ?? [];

	return {
		id: node.id,
		slug: node.slashCommand?.split('/')?.[1] ?? '',
		name: node.name ?? '',
		description: node.description ?? '',
		iconName: node.iconUrl?.split('name/')?.[1] as SkillIconName,
		product: displayName ? displayName.charAt(0).toUpperCase() + displayName.slice(1) : '',
		productIconUrl: node.integrationKey?.iconUrl ?? '',
		...(node.color ? { color: node.color as Skill['color'] } : {}),
		...(tagDisplayNames.length > 0 ? { tags: [...tagDisplayNames] } : {}),
	};
}

/**
 * Parses a GraphQL response containing skill directory item edges into Skill[].
 */
export function parseSkillEdges(
	edges:
		| ReadonlyArray<
				| {
						readonly node?:
							| SkillDirectoryItemNode
							| { readonly __typename: string; readonly [key: string]: unknown }
							| null
							| undefined;
				  }
				| null
				| undefined
		  >
		| null
		| undefined,
): Skill[] {
	if (!edges) {
		return [];
	}
	return edges
		.map((edge) => parseSkillNode(edge?.node as SkillDirectoryItemNode | null))
		.filter((skill): skill is Skill => skill !== null);
}
